import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Grid,
  Paper,
  Alert,
  useTheme,
  useMediaQuery,
  Divider,
  Skeleton,
} from "@mui/material";

const NotificationPage = () => {
  const { accessToken } = useAuth();
  const [user, setUser] = useState(null);
  const [sites, setSites] = useState([]);
  const [siteDetails, setSiteDetails] = useState([]);
  const [groups, setGroups] = useState([]);
  const [devices, setDevices] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const baseURL = process.env.REACT_APP_BASEURL;

  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Fetch data in sequence
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Step 1: Fetch all sites
        const sitesRes = await axios.get(`${baseURL}/get-sites/`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const fetchedSites = sitesRes.data.data || [];
        setSites(fetchedSites);

        // Step 2: Fetch user details
        const userRes = await axios.get(`${baseURL}/users`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const userData = userRes.data[0]; // Extract first user
        setUser(userData);

        // Filter sites by user's sites
        const userSiteIds = userData.sites || [];
        const userSites = fetchedSites.filter((site) =>
          userSiteIds.includes(site.id)
        );

        // Step 3: Fetch site details
        const siteDetailsPromises = userSites.map((site) =>
          axios.get(`${baseURL}/sites/${site.id}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          })
        );
        const siteDetailsRes = await Promise.all(siteDetailsPromises);
        const siteDetailsData = siteDetailsRes.map((res) => res.data);
        setSiteDetails(siteDetailsData);

        // Step 4: Fetch all groups and filter by user's sites
        const groupsRes = await axios.get(`${baseURL}/groups`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const allGroups = groupsRes.data || [];
        const userGroups = allGroups.filter((group) =>
          userSiteIds.includes(group.site)
        );
        setGroups(userGroups);

        // Step 5: Fetch devices for each site
        const devicePromises = userSites.map((site) =>
          axios.get(`${baseURL}/devices/?site=${site.id}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          })
        );
        const deviceRes = await Promise.all(devicePromises);
        const devicesData = deviceRes.map((res, index) => ({
          siteId: userSites[index].id,
          devices: res.data || [],
        }));
        setDevices(devicesData);

        // Step 6: Fetch notifications for each site
        const notificationPromises = userSites.map((site) =>
          axios.get(
            `${baseURL}/alerts/notifications/?site=${site.id}`,
            {
              headers: { Authorization: `Bearer ${accessToken}` },
            }
          )
        );
        const notificationRes = await Promise.all(notificationPromises);
        const notificationsData = notificationRes.map((res, index) => ({
          siteId: userSites[index].id,
          notifications: res.data || [],
        }));
        setNotifications(notificationsData);

        // Step 7: Fetch rules for each site
        const rulePromises = userSites.map((site) =>
          axios.get(`${baseURL}/alerts/rules/?site=${site.id}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          })
        );
        const ruleRes = await Promise.all(rulePromises);
        const rulesData = ruleRes.map((res, index) => ({
          siteId: userSites[index].id,
          rules: res.data || [],
        }));
        setRules(rulesData);
      } catch (error) {
        console.error(
          "Error fetching data:",
          error.response ? error.response.data : error.message
        );
        setError(
          "Failed to load notifications or related data. Please try again later."
        );
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [accessToken]);

  const skeletonSites = Array(3).fill(0);

  if (loading)
    return (
      <Box sx={{ p: 0 }}>
        <Skeleton variant="text" width="20%" height={36} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="35%" height={24} sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          {skeletonSites.map((_, index) => (
            <Grid item xs={12} key={index} width={400}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 2,
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                }}
              >
                {/* Site header */}
                <Skeleton
                  variant="text"
                  width="50%"
                  height={32}
                  sx={{ mb: 2 }}
                />

                {/* Site details */}
                <Box sx={{ mb: 3 }}>
                  <Skeleton variant="text" width="100%" height={24} />
                  <Skeleton variant="text" width="100%" height={24} />
                  <Skeleton variant="text" width="100%" height={24} />
                </Box>

                {/* Notifications section */}
                <Skeleton
                  variant="text"
                  width="40%"
                  height={32}
                  sx={{ mb: 2 }}
                />
                <Box>
                  <Skeleton variant="text" width="70%" height={24} />
                  <Skeleton variant="text" width="70%" height={24} />
                  <Skeleton variant="text" width="70%" height={24} />
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Alert Rules section */}
                <Skeleton
                  variant="text"
                  width="40%"
                  height={32}
                  sx={{ mb: 2 }}
                />
                <Box>
                  <Skeleton variant="text" width="70%" height={24} />
                  <Skeleton variant="text" width="70%" height={24} />
                  <Skeleton variant="text" width="70%" height={24} />
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    );

  if (error)
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );

  return (
    <Box sx={{ p: 0 }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontSize: { xs: "1.75rem", md: "2.125rem" } }}
      >
        Notifications
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {user
          ? `Viewing notifications for ${user.username}`
          : "Loading user data..."}
      </Typography>

      {siteDetails.length > 0 ? (
        <Grid container spacing={3}>
          {siteDetails.map((site) => {
            const siteNotifications =
              notifications.find((n) => n.siteId === site.id)?.notifications ||
              [];
            const siteRules =
              rules.find((r) => r.siteId === site.id)?.rules || [];
            const siteDevices =
              devices.find((d) => d.siteId === site.id)?.devices || [];
            // Filter groups by site ID or device associations
            const siteGroups = groups.filter(
              (g) =>
                g.site === site.id ||
                siteDevices.some((d) => d.groups?.includes(g.id))
            );

            return (
              <Grid item xs={12} key={site.id}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, fontSize: { xs: "1.1rem", md: "1.25rem" } }}
                  >
                    {site.name}
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Latitude: {site.latitude} | Longitude: {site.longitude}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Groups:{" "}
                      {siteGroups.length > 0
                        ? siteGroups.map((g) => g.name).join(", ")
                        : "None"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Devices: {site.device_count}
                    </Typography>
                  </Box>

                  {/* Notifications Table */}
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 1, fontWeight: "medium" }}
                  >
                    Notifications
                  </Typography>
                  {siteNotifications.length > 0 ? (
                    <Box sx={{ overflowX: "auto", mb: 3 }}>
                      <Table sx={{ minWidth: { xs: 300, sm: 600 } }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Rule ID</TableCell>
                            <TableCell>Start</TableCell>
                            <TableCell>Resolved</TableCell>
                            <TableCell>Acknowledged</TableCell>
                            <TableCell>Measurements</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {siteNotifications.map((notification) => (
                            <TableRow key={notification.id}>
                              <TableCell>{notification.id}</TableCell>
                              <TableCell>{notification.rule_id}</TableCell>
                              <TableCell>
                                {new Date(notification.start).toLocaleString()}
                              </TableCell>
                              <TableCell>
                                {notification.resolved
                                  ? new Date(
                                      notification.resolved
                                    ).toLocaleString()
                                  : "Open"}
                              </TableCell>
                              <TableCell>
                                {notification.acknowledged
                                  ? new Date(
                                      notification.acknowledged
                                    ).toLocaleString()
                                  : "No"}
                              </TableCell>
                              <TableCell>
                                {notification.measurements.length}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 3 }}
                    >
                      No notifications for this site.
                    </Typography>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* Rules Table */}
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 1, fontWeight: "medium" }}
                  >
                    Alert Rules
                  </Typography>
                  {siteRules.length > 0 ? (
                    <Box sx={{ overflowX: "auto" }}>
                      <Table sx={{ minWidth: { xs: 300, sm: 600 } }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Group ID</TableCell>
                            <TableCell>Enabled</TableCell>
                            <TableCell>Retrigger</TableCell>
                            <TableCell>Conditions</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {siteRules.map((rule) => (
                            <TableRow key={rule.id}>
                              <TableCell>{rule.name}</TableCell>
                              <TableCell>{rule.group_id}</TableCell>
                              <TableCell>
                                {rule.enabled ? "Yes" : "No"}
                              </TableCell>
                              <TableCell>
                                {rule.retrigger ? "Yes" : "No"}
                              </TableCell>
                              <TableCell>
                                {rule.conditions.map((cond, idx) => (
                                  <Box key={idx}>
                                    {cond.measurement_type}: {cond.low_limit} -{" "}
                                    {cond.high_limit} (
                                    {cond.inside_limits ? "Inside" : "Outside"})
                                  </Box>
                                ))}
                              </TableCell>
                              <TableCell>
                                {rule.actions.map((action, idx) => (
                                  <Box key={idx}>Email: {action.email}</Box>
                                ))}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No alert rules for this site.
                    </Typography>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Typography variant="body1" color="text.secondary">
          No sites found.
        </Typography>
      )}
    </Box>
  );
};

export default NotificationPage;
